/**
 * Pre-render scrub for mermaid diagrams.
 *
 * Mirrors the backend `app/agent/content_scrub.py:fix_mermaid_unquoted_labels`,
 * plus two robustness fixes for LLM-emitted diagrams:
 *   1. literal `\n` inside node/edge labels → `<br/>` (mermaid's line break;
 *      a raw `\n` in a label is a parse error in the strict lexer).
 *   2. TRUNCATED diagrams — when the model got cut off mid-node (and even the
 *      closing ``` fence is missing), drop the incomplete trailing line(s) and
 *      close the fence so the valid prefix still renders instead of erroring.
 *
 * The mermaid lexer treats `@`, `(`, `)`, `[`, `]`, `{`, `}`, `#`, `&`, `;`,
 * `,`, `*`, `<`, `>` as special at the top level; they're only safe inside a
 * `"`-quoted text state. Quoting bypasses all of them at once.
 */

const SPECIAL_CHARS = /[@()\[\]{}#&;,*<>]/;

function quoteEdgeLabel(_match: string, body: string): string {
  if (body.startsWith('"')) return _match;
  const escaped = body.replace(/"/g, '\\"');
  return `|"${escaped}"|`;
}

function quoteNodeText(prefix: string, body: string, suffix: string): string {
  if (body.startsWith('"')) return `${prefix}${body}${suffix}`;
  if (!SPECIAL_CHARS.test(body)) return `${prefix}${body}${suffix}`;
  const escaped = body.replace(/"/g, '\\"');
  return `${prefix}"${escaped}"${suffix}`;
}

/** Drop trailing lines with unbalanced brackets/quotes (the truncation point). */
function dropTruncatedTail(body: string): string {
  const lines = body.split("\n");
  while (lines.length) {
    const last = lines[lines.length - 1];
    const opens =
      (last.match(/[[({]/g) || []).length;
    const closes =
      (last.match(/[\])}]/g) || []).length;
    const quotes = (last.match(/"/g) || []).length;
    // A complete line balances its brackets and has even quotes. An incomplete
    // (truncated) trailing node like `T1["Exper` opens more than it closes.
    if (opens > closes || quotes % 2 === 1) {
      lines.pop();
      continue;
    }
    break;
  }
  return lines.join("\n").replace(/\s+$/, "");
}

/**
 * Convert markdown links `[text](url)` → `text` inside a diagram body, but ONLY
 * when the url is a real link (contains `://`, or starts with `#`/`artifact`).
 * The model sometimes embeds `[label](artifact://<id>)` in a node label; the id
 * (digits/colons) then leaks into the mermaid lexer and crashes it ("got '1'").
 * Mermaid node text is `A[label]` with no following `(`, so a `]` immediately
 * followed by `(` is a genuine markdown link — safe to flatten. Plain string
 * scan (no regex).
 */
function isLinkUrl(url: string): boolean {
  const u = url.trim();
  return u.includes("://") || u.startsWith("#") || u.startsWith("artifact") || u.startsWith("mailto:");
}

function stripInlineMarkdownLinks(body: string): string {
  // Pass 1 — labeled links `[label](url)` → `label`.
  let out = "";
  let i = 0;
  while (i < body.length) {
    if (body[i] === "[") {
      let k = i + 1;
      while (k < body.length && body[k] !== "]" && body[k] !== "\n") k++;
      if (k < body.length && body[k] === "]" && body[k + 1] === "(") {
        let q = k + 2;
        const urlStart = q;
        while (q < body.length && body[q] !== ")" && body[q] !== "\n") q++;
        if (q < body.length && body[q] === ")" && isLinkUrl(body.slice(urlStart, q))) {
          out += body.slice(i + 1, k); // keep only the label text
          i = q + 1;
          continue;
        }
      }
    }
    out += body[i];
    i++;
  }
  // Pass 2 — bare `(url)` parens (no label) → removed; and any leftover bare
  // `scheme://token` not in parens → removed. A round mermaid node `A(text)` is
  // safe because its content isn't a link url.
  let res = "";
  let j = 0;
  const DELIMS = " \t\n\"')]}|";
  while (j < out.length) {
    if (out[j] === "(") {
      let q = j + 1;
      while (q < out.length && out[q] !== ")" && out[q] !== "\n") q++;
      if (q < out.length && out[q] === ")" && isLinkUrl(out.slice(j + 1, q))) {
        j = q + 1; // drop the whole `(url)`
        continue;
      }
    }
    if (
      out.slice(j, j + 11) === "artifact://" ||
      out.slice(j, j + 7) === "http://" ||
      out.slice(j, j + 8) === "https://"
    ) {
      let q = j;
      while (q < out.length && !DELIMS.includes(out[q])) q++;
      j = q; // drop the bare scheme + token
      continue;
    }
    res += out[j];
    j++;
  }
  return res;
}

function scrubBody(body: string): string {
  // xychart-beta uses [...] for numeric data arrays — quoting their contents
  // (because they contain commas) turns numbers into strings and breaks the parser.
  // Skip passes 1-3 for xychart diagrams; only truncation repair still applies.
  const isXyChart = body.trimStart().startsWith("xychart-beta");

  // 0. flatten any embedded markdown links so artifact ids never reach the lexer.
  let scrubbed = stripInlineMarkdownLinks(body);

  if (!isXyChart) {
    // 1. literal `\n` in labels → mermaid line break (must precede quoting so the
    //    inserted `<br/>` triggers the special-char quoting below).
    scrubbed = scrubbed.replace(/\\n/g, "<br/>");
    // 2. edge labels |...|
    scrubbed = scrubbed.replace(/\|([^|\n]+?)\|/g, quoteEdgeLabel);
    // 3. node text A[..], A(..), A{..} — quote when it has lexer-special chars
    scrubbed = scrubbed.replace(/(\[)([^\]\n]+?)(\])/g, (_m, p, b, s) => quoteNodeText(p, b, s));
    scrubbed = scrubbed.replace(/(\()([^)\n]+?)(\))/g, (_m, p, b, s) => quoteNodeText(p, b, s));
    scrubbed = scrubbed.replace(/(\{)([^}\n]+?)(\})/g, (_m, p, b, s) => quoteNodeText(p, b, s));
  }

  // 4. repair truncation (drop the incomplete tail)
  scrubbed = dropTruncatedTail(scrubbed);
  return scrubbed;
}

/**
 * Scrub all mermaid code blocks. Handles closed ```mermaid…``` blocks AND a
 * trailing UNCLOSED block (truncated message) — non-mermaid blocks untouched.
 */
export function fixMermaidUnquotedLabels(text: string): string {
  if (!text || !text.includes("```mermaid")) return text;

  // Closed blocks.
  let out = text.replace(/```mermaid\n([\s\S]*?)\n```/g, (_match, body: string) => {
    return "```mermaid\n" + scrubBody(body) + "\n```";
  });

  // Trailing UNCLOSED block (the model got cut off before the closing fence).
  const open = out.lastIndexOf("```mermaid");
  if (open !== -1) {
    const after = out.slice(open + "```mermaid".length);
    if (!after.includes("```")) {
      const raw = after.startsWith("\n") ? after.slice(1) : after;
      // An unclosed fence runs to end-of-doc, so it can swallow trailing markdown
      // (e.g. an "## Evidence Artifacts" section with [..](artifact://..) links —
      // whose balanced brackets survive dropTruncatedTail and then crash the
      // mermaid lexer). Split the diagram from the markdown tail, close the fence
      // after the diagram, and re-append the tail OUTSIDE the fence so it renders.
      const diagram = mermaidHead(raw);
      const tail = raw.slice(diagram.length);
      out = out.slice(0, open) + "```mermaid\n" + scrubBody(diagram) + "\n```" + tail;
    }
  }
  return out;
}

/** Lines that begin real markdown (not mermaid) — where an unclosed diagram ends. */
function isNonMermaidLine(line: string): boolean {
  const t = line.trim();
  if (!t) return false;
  return (
    t.startsWith("#") || // heading
    t.startsWith("- [") ||
    t.startsWith("* [") ||
    t.startsWith("```") || // a new fence
    t.includes("](artifact://") ||
    t.includes("](http")
  );
}

/** Return the leading run of `body` up to (not including) the first markdown line. */
function mermaidHead(body: string): string {
  const lines = body.split("\n");
  const kept: string[] = [];
  for (const line of lines) {
    if (isNonMermaidLine(line)) break;
    kept.push(line);
  }
  return kept.join("\n");
}
